/**
 * Start a newly-navigated route at the top of the page.
 *
 * THE BUG THIS FIXES, measured rather than assumed. The contextual top bar
 * (m5t-primary-nav) is `position: sticky; top: 0` with an opaque background in TABBED
 * mode, and nothing in the app ever reset the scroll position on navigation. So clicking
 * a tab while scrolled — which is the natural thing to do, because the tabs live IN the
 * sticky bar and are therefore still on screen — rendered the next page without moving
 * the viewport. The new page's header then sat inside the bar's 61px band:
 *
 *     scroll  navBottom  headingTop   result
 *          0         61         101   fully visible
 *         60         61          41   CLIPPED along y=61 (heading + period toggle)
 *        100         61           1   fully hidden behind the bar
 *
 * That "clipped along a straight horizontal line" is the reported symptom on Ad
 * Performance, and it is not the sticky bar misbehaving: overlap on scroll is what a
 * sticky header does, and m5t-primary-nav's own comment warns against "fixing" a
 * recurrence with padding-top for exactly that reason. The defect is arriving at a new
 * route already scrolled.
 *
 * WHY IT LOOKED LIKE A TABBED-MODE BUG. Spacer-mode pages have the same scroll behaviour,
 * but 5f8754a made that bar transparent with pointer-events:none, so content under it
 * shows through and nobody sees a clip. Tabbed mode paints an opaque bar over the same
 * band. Same underlying cause, visible in only one of the two variants — which is why the
 * earlier fix did not cover this.
 */

/**
 * What actually scrolls.
 *
 * #app is the app's scroll pane — the shell is height:100dvh/overflow:hidden and the
 * content region owns its own scrollport, so that the chrome above it can never be
 * scrolled under. window.scrollTo is a NO-OP in that arrangement: the document does not
 * scroll, so scrolling it moves nothing and the new route renders at whatever offset the
 * previous one was left at.
 *
 * Falls back to the window when there is no pane, which keeps this working for any
 * surface that still scrolls the document (and for tests that stub a scroller).
 */
function defaultScroller() {
  if (typeof document !== 'undefined') {
    const pane = document.getElementById('app');
    // Only treat it as the scroller if it really is one — during early boot, or in a host
    // page that lays #app out differently, scrolling a non-scrolling element silently
    // does nothing and the window is the right target.
    if (pane && typeof pane.scrollTo === 'function') return pane;
  }
  return typeof window !== 'undefined' ? window : null;
}

/** Last route we scrolled for. Module state, matching routeHandoff.js's approach. */
let lastRoute = null;

/**
 * Reset scroll if this is a genuinely NEW route.
 *
 * Guarded on the route CHANGING, not merely on the event firing: ROUTE_NAV_AFTER is also
 * published when the same route re-renders (a scope change calls reloadCurrentRoute(),
 * and several pages re-render in place). Yanking the viewport to the top while someone is
 * reading halfway down a list they just filtered would be a worse bug than the one this
 * fixes.
 *
 * TRADE-OFF, STATED PLAINLY: this also resets on browser Back, so returning to a long
 * list loses your place. Restoring per-entry scroll needs a history-keyed position store
 * and a way to tell Back from a fresh navigation, which is a good deal more machinery
 * than the problem currently justifies. Landing at the top is at least predictable, and
 * never hides the page heading behind the bar.
 *
 * @param {string} routeName
 * @param {{scroller?: {scrollTo?: Function}}} [deps] - injection seam for tests, same
 *   convention as the rest of the app; production passes nothing and gets `window`.
 * @returns {boolean} whether it actually scrolled
 */
export function resetScrollForRoute(routeName, deps = {}) {
  if (!routeName || routeName === lastRoute) return false;
  lastRoute = routeName;
  const scroller = deps.scroller || defaultScroller();
  // jsdom has no layout and its window.scrollTo throws "Not implemented"; a missing or
  // partial scroller must never take down a route render over a cosmetic concern.
  if (!scroller || typeof scroller.scrollTo !== 'function') return false;
  try {
    // 'instant', not 'smooth': a route change should already be at the top when the new
    // page paints. Animating it would show the previous scroll position first, which is
    // the very frame this exists to prevent.
    scroller.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  } catch {
    try { scroller.scrollTo(0, 0); } catch { /* nothing more to try */ }
  }
  return true;
}

/** Test-only: forget the last route so cases start clean. */
export function _resetRouteScrollState() {
  lastRoute = null;
}

export default { resetScrollForRoute, _resetRouteScrollState };
